import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ClarityService } from './clarity.service';
import { SetLanguage } from '../state/ui.actions';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private static readonly LOCALE_MAP: { [key: string]: string } = {
    en: 'en-US',
    no: 'no-NO',
    se: 'sv-SE',
    is: 'is-IS',
    nl: 'nl-NL',
  };

  /** Maps app language codes to MAT_DATE_LOCALE (Moment/DateAdapter) format */
  private static readonly MAT_DATE_LOCALE_MAP: { [key: string]: string } = {
    en: 'en',
    no: 'nb',
    se: 'sv',
    is: 'is',
    nl: 'nl',
  };

  constructor(
    private translate: TranslateService,
    private store: Store,
    private clarityService: ClarityService
  ) {}

  /**
   * Initialize language from localStorage and set it in the store
   */
  initializeLanguage(): void {
    // Clear any corrupted language data first
    this.clearCorruptedLanguageData();

    const localStorageLanguage = localStorage.getItem('language');
    let language = localStorageLanguage || 'en';

    // Clean up any corrupted language data (remove quotes if present)
    if (language.startsWith('"') && language.endsWith('"')) {
      language = language.slice(1, -1);
    }

    // Set the language in the translate service
    this.translate.use(language);

    // Dispatch action to store the language in NGXS state
    this.store.dispatch(new SetLanguage(language));
    this.clarityService.setLanguageTag(language);
  }

  /**
   * Clear any corrupted language data from localStorage
   */
  private clearCorruptedLanguageData(): void {
    const language = localStorage.getItem('language');
    if (
      language &&
      (language.includes('\\') || language.startsWith('"') || language.includes('"'))
    ) {
      localStorage.removeItem('language');
    }
  }

  /**
   * Change the application language
   * @param language - The language code to set
   */
  setLanguage(language: string): void {
    // Update translate service
    this.translate.use(language);

    // Dispatch action to store the language in NGXS state (which will also update localStorage)
    this.store.dispatch(new SetLanguage(language));
    this.clarityService.setLanguageTag(language);
  }

  /**
   * Get the current language from localStorage
   * @returns The current language code
   */
  getCurrentLanguage(): string {
    const language = localStorage.getItem('language') || 'en';

    // Clean up any corrupted language data (remove quotes if present)
    if (language.startsWith('"') && language.endsWith('"')) {
      const cleanedLanguage = language.slice(1, -1);
      // Update localStorage with cleaned value
      localStorage.setItem('language', cleanedLanguage);
      return cleanedLanguage;
    }

    return language;
  }

  /**
   * Maps app language codes to locale codes for date/number formatting
   */
  getLocaleForLanguage(language: string): string {
    return LanguageService.LOCALE_MAP[language] || 'en-US';
  }

  /**
   * Maps app language codes to MAT_DATE_LOCALE for Angular Material datepicker
   */
  getMatDateLocaleForLanguage(language: string): string {
    return LanguageService.MAT_DATE_LOCALE_MAP[language] ?? 'en';
  }
}
